import { useState, useEffect, useRef, useCallback } from "react";
import {
  BsChevronDown,
  BsInfoCircle,
  BsX,
  BsXCircleFill,
} from "react-icons/bs";
import { DilDropdown } from "./DilDropdown";
import { OverlayTrigger } from "react-bootstrap";
import { TooltipStandard } from "./TooltipStandard";

export const Combobox = ({
  label,
  labelStyle,
  labelClass,
  required,
  infoText,
  validate,
  validationTestOnBlur,
  wasSubmitted,
  options, //string[] || {display:"", value:""}[] //if a string array is passed, it will automatically be transformed to an array of {display:"", value:""} where both display and value are the same
  selectedOptions,
  setSelectedOptions,
  onSelectOption, // (display, value) => {} //Use this if you need to have a separate display and value for your options.
  currentSearchTerm,
  setCurrentSearchTerm, //Setter for the state variable. You can pass a custom function but note that the parameter will be passed the new value currently, not an event.
  currentValue,
  onClearCurrentSearchTerm, //()=>{} called when option is cleared (x button next to input is pressed)
  loading,
  multiselect = false,
  selectFromOptionsOnly,
  placeholder = "",
  categories = [],
  currentCategory,
  categoryDropdownContainerStyle,
  setCurrentCategory,
  narrowResultsByStartsWith = false,
  showNoResultsMessage = true,
  noResultsMessage = "No Results Found",
  noResultsMessageStyleMerge,
  dropdownProps,
  autofilter = true,
  autofilterTrim, //trims search terms and suggestions, so suggestions won't be hidden by extra whitespace around term. (usually happens when user copy/pastes)
  trimOnSelect, //trims both manually entered terms or suggestion upon selection.
  caseSensitive,
  maxToDisplayAtOnce,
  onClickComboboxInput,
  styleInput,
  classInput,
  styleInputContainer,
  classInputContainer,
  comboboxBackgroundColor = "rgb(245,245,245)",
  comboboxBorderRadius = "20px",
}) => {
  const comboboxContainerRef = useRef();
  const [comboboxFocused, setComboboxFocused] = useState(false);
  // const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const handleChangeSearchTerm = async (e) => {
    const value = e.target.value;
    //filtering the barcodes passed length of barcodeSearchTermMinLength is done above on setting filteredPossibleBarcodes //After removing the below attempts at optimization, this is no longer the case and we just show whatever the backend sends on every (debounced) onChange event
    setCurrentSearchTerm(value);
  };
  const handleClearSearchTerm = () => {
    setCurrentSearchTerm("");
    onClearCurrentSearchTerm && onClearCurrentSearchTerm();

    // setBarcodeComboboxFocused(false);
  };

  const handleOnClickComboboxInput = () => {
    onClickComboboxInput && onClickComboboxInput();
    if (!comboboxFocused) {
      setComboboxFocused(true);
    }
  };

  const handleFocusCombobox = (e) => {
    setComboboxFocused(true);
    // setShowTooltip(false);
  };

  const handleSelectOption = (item) => {
    if (multiselect) {
      setSelectedOptions([
        ...selectedOptions,
        trimOnSelect ? item.value.trim() : item,
      ]);
    } else {
      // console.log(item);
      onSelectOption
        ? onSelectOption(item.display, item.value)
        : setCurrentSearchTerm(item.display); // may also want to have a setSelectedValue prop/state as well? or just make it so onSelectOption is required and the only way to do things.
      setComboboxFocused(false);
    }
  };
  const handleComboboxKeyDown = (e) => {
    if (e.key === "Enter" && multiselect && !selectFromOptionsOnly) {
      if (currentSearchTerm.trim() === "") {
        return;
      }
      e.preventDefault();
      setSelectedOptions([
        ...selectedOptions,
        trimOnSelect ? currentSearchTerm.trim() : currentSearchTerm,
      ]);
      setCurrentSearchTerm("");
    }
  };
  const handleClickOutOfCombobox = useCallback(
    (e) => {
      // console.log(e.target);
      // console.log(barcodeComboboxRef.current);
      // console.log(e.target === barcodeComboboxInputRef.current);
      // console.log(e.target.classList.contains("noes-dropdown-item"));
      if (
        comboboxContainerRef.current &&
        !comboboxContainerRef.current.contains(e.target)
      ) {
        // console.log("clicked outside");
        setComboboxFocused(false);
        if (selectFromOptionsOnly) {
          if (!currentValue) {
            setCurrentSearchTerm("");
          } else {
            setCurrentSearchTerm(
              optionsTransformed.find((option) => {
                return option.value === currentValue;
              }).display
            );
          }
        }
      }
    },
    [currentValue]
  );

  const escapeCombobox = useCallback(function escapeCombobox(e) {
    // console.log(e.key);
    if (e.key === "Escape") {
      setComboboxFocused(false);
    }
  }, []);

  useEffect(() => {
    //"mousedown" fixed the issues "click" was having (click works more like mouseup)
    document.addEventListener("mousedown", handleClickOutOfCombobox);
    document.addEventListener("keydown", escapeCombobox);
    return () => {
      document.removeEventListener("mousedown", handleClickOutOfCombobox);
      document.removeEventListener("keydown", escapeCombobox);
    };
  }, [handleClickOutOfCombobox]);

  const handleDeleteFromSelectedArray = (item) => {
    // console.log(item);
    const arrayToUpdate = [...selectedOptions];
    arrayToUpdate.splice(arrayToUpdate.indexOf(item), 1);
    setSelectedOptions(arrayToUpdate);
  };

  const handleClearAllSelectedOptions = (e) => {
    setSelectedOptions([]);
    //In case we want to reset the whole box on clear:
    // setBarcodeSearchValue("");
    // setPossibleBarcodes([]);
    // setBarcodeComboboxFocused(false) //not sure if this is wanted
  };
  // console.log("options", options);
  const optionsTransformed =
    typeof options?.[0] === "string"
      ? options?.map((option) => {
          return { display: option, value: option };
        })
      : options;
  const filteredOptions =
    autofilter && currentSearchTerm.length > 0
      ? optionsTransformed && optionsTransformed.length > 0
        ? optionsTransformed.filter((option) => {
            let optionToCompare = option.display;
            let searchTermToCompare = currentSearchTerm;
            if (!caseSensitive) {
              optionToCompare = optionToCompare.toLowerCase();
              searchTermToCompare = searchTermToCompare.toLowerCase();
            }
            if (autofilterTrim) {
              optionToCompare = optionToCompare.trim();
              searchTermToCompare = searchTermToCompare.trim();
            }
            return (
              (narrowResultsByStartsWith
                ? optionToCompare
                    .toLowerCase()
                    .startsWith(searchTermToCompare.toLowerCase())
                : optionToCompare
                    .toLowerCase()
                    .includes(searchTermToCompare.toLowerCase())) &&
              !selectedOptions?.includes(option.value)
            );
          })
        : []
      : optionsTransformed.filter((option) => {
          return !selectedOptions?.includes(option.value);
        });
  // console.log(filteredOptions);

  //validation logic
  const [startedTyping, setStartedTyping] = useState(false);
  const [wasBlurred, setWasBlurred] = useState(false);
  const validationTest = () => {
    if (multiselect) {
      //
    } else {
      if (selectFromOptionsOnly) {
        return true;
      } else {
        if (
          options.find((option) => {
            return (option.value = currentValue);
          })
        ) {
          return true;
        } else {
          return false;
        }
      }
    }
  };

  const isValid =
    (validate ? validationTest() : true) ||
    (validationTestOnBlur
      ? !wasBlurred && !wasSubmitted
      : !startedTyping && !wasSubmitted);

  return (
    <div>
      {label && (
        <label
          style={labelStyle}
          className={(labelClass ?? "") + (isValid ? "" : " text-danger")}
        >
          {label}
          {required ? <span className="required-star">*</span> : ""}
          {infoText && (
            <OverlayTrigger
              placement="top"
              flip
              overlay={<TooltipStandard>{infoText}</TooltipStandard>}
            >
              <div style={{ display: "inline" }}>
                {" "}
                <BsInfoCircle className="info-circle-color" />
              </div>
            </OverlayTrigger>
          )}
          {loading && (
            <>
              {" "}
              <div
                className="spinner-border spinner-border-sm text-secondary"
                role="status"
              >
                <span className="sr-only">Loading...</span>
              </div>
            </>
          )}
        </label>
      )}
      <div
        style={{
          border: "1px solid rgba(0,0,0,0.2)",
          minHeight: "2rem",
          padding: "2px",
          borderRadius: comboboxBorderRadius,
        }}
        ref={comboboxContainerRef}
      >
        <div style={{ padding: selectedOptions?.length > 0 ? "2px" : 0 }}>
          {selectedOptions?.map((item, index) => (
            <div
              key={index + "-" + item}
              className="no-copy"
              style={{
                margin: "2px",
                paddingTop: "0px",
                paddingBottom: "0px",
                borderRadius: "10000px",
                border: "1px solid rgba(0,0,0,.2)",
                display: "inline-block",
                paddingLeft: "6px",
                paddingRight: "5px",
                fontSize: ".9em",
                wordBreak: "break-word",
              }}
            >
              {item}
              <BsXCircleFill
                className="clickable"
                fill="rgb(110,110,110)"
                onClick={() => {
                  handleDeleteFromSelectedArray(item);
                }}
                style={{ marginLeft: "10px", marginTop: "-3px" }}
              />
            </div>
          ))}
          {selectedOptions?.length > 0 && (
            <button
              onClick={handleClearAllSelectedOptions}
              style={{
                margin: "2px",
                paddingTop: "0px",
                paddingBottom: "0px",
                borderRadius: "10000px",
                border: "1px solid rgba(0,0,0,.2)",
                display: "inline-block",
                paddingLeft: "6px",
                paddingRight: "5px",
                backgroundColor: "rgb(235,235, 235)",
                outline: "none",
                fontSize: ".9em",
                // border: "none",
              }}
            >
              Clear all
            </button>
          )}
        </div>
        <div className="noes-dropdown">
          <div style={{ width: "100%", padding: "2px" }}>
            <div
              style={{
                // padding: "5px",
                width: "100%",
                display: "flex",
                alignItems: "stretch",
                backgroundColor: comboboxBackgroundColor,
                // backgroundColor: "blue",
                borderRadius: "20px",
              }}
            >
              {categories?.length > 0 && (
                <DilDropdown
                  options={categories}
                  selectedOption={currentCategory}
                  setSelectedOption={setCurrentCategory}
                  styleMergeDropdownContainer={{
                    borderRadius: "20px 0 0 20px",
                    // display: "flex",
                    // alignItems: "center",
                    // paddingLeft: "1rem",
                    // paddingRight: ".5rem",
                  }}
                  styleMergeDropdown={{
                    padding: "0",
                    height: "100%",
                    paddingLeft: "1rem",
                    paddingRight: ".3rem",
                    width: "6.5rem",
                    // backgroundColor: "red",
                    // height: "100%",
                    // // width: "70px",
                    // textOverflow: "ellipsis",
                  }}
                  styleMergeOptionContainer={{
                    // align: "left",
                    minWidth: "0",
                    left: ".5rem",
                    right: "0rem",
                  }}
                  styleMergeDropdownOption={{
                    wordBreak: "break-word",
                  }}
                  {...dropdownProps}
                  // customIcon={(props)=>{
                  //   return <BsChevronDown {...props}/>
                  // }}
                />
              )}
              <div style={{ position: "relative", width: "100%" }}>
                <div style={{ width: "100%" }}>
                  <div
                    style={
                      styleInputContainer
                        ? styleInputContainer
                        : {
                            // padding: "5px",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            // backgroundColor: "rgb(245,245,245)",
                            // backgroundColor: "blue",
                            borderRadius: "20px",
                          }
                    }
                  >
                    <input
                      className="form-control-sm"
                      placeholder={
                        categories?.length > 0
                          ? `Search by ${currentCategory}`
                          : placeholder
                      }
                      size="1" //https://stackoverflow.com/a/29990524 - Without this most input types won't go below approximately 100px in width (unless you set width/max-width explicitly which we probably don't want, i.e. we want to let the input take up 100% width but also be able to go below 100px when shrinking the screen.)
                      style={
                        styleInput
                          ? styleInput
                          : {
                              border: "0px",
                              backgroundColor: comboboxBackgroundColor,
                              // backgroundColor: "green",
                              flex: 1,
                              outline: "none",
                              // borderRadius: "20px",
                              marginLeft: "5px",
                            }
                      }
                      type="text"
                      onChange={handleChangeSearchTerm}
                      onClick={handleOnClickComboboxInput}
                      onFocus={handleFocusCombobox}
                      onKeyDown={handleComboboxKeyDown}
                      // onMouseEnter={handleMouseEnterTooltip}
                      // onMouseLeave={handleMouseLeaveTooltip}
                      // onMouseMove={handleTooltipMouseMove}
                      value={currentSearchTerm}
                    ></input>
                    <div
                      style={{
                        width: "2em",
                        // height: "calc(1.5em + 0.75rem + 2px)", //bootstrap form-control height
                        height: "calc(1.5em + 0.5rem + 2px)",
                        textAlign: "center",
                        // backgroundColor: "yellow",
                        display: "grid",
                        placeContent: "center",
                      }}
                    >
                      {loading ? (
                        <div
                          className="spinner-border"
                          style={{
                            borderWidth: ".15em",
                            color: "gray",
                            width: "1em",
                            height: "1em",
                            // backgroundColor: "green",
                          }}
                          role="status"
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                      ) : currentSearchTerm.length > 0 ? (
                        // <div
                        //   style={{
                        //     borderWidth: ".15em",
                        //     color: "gray",
                        //     width: "1em",
                        //     height: "1em",
                        //     // backgroundColor: "purple",
                        //   }}
                        //   role="clear"
                        // >
                        <button
                          type="button"
                          style={{
                            border: "none",
                            outline: "none",
                            padding: 0,
                            // display: "block",
                            width: "2em", //width 100% and height 100% were not making the button fill up it's parent (that did have these same width and height set) for some reason
                            height: "calc(1.5em + 0.75rem + 2px)",
                            backgroundColor: "rgba(0,0,0,0)",
                            display: "grid",
                            placeContent: "center",
                          }}
                          onClick={handleClearSearchTerm}
                        >
                          <BsX size={18} color="gray" />
                        </button>
                      ) : (
                        // </div>
                        ""
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className="noes-dropdown-content"
                  style={{
                    display: comboboxFocused ? "block" : "none",
                  }}
                >
                  {filteredOptions.length > 0 ? (
                    <>
                      {filteredOptions
                        .slice(0, maxToDisplayAtOnce ?? filteredOptions.length)
                        .map((item, index) => (
                          <div
                            key={index + "-" + item.value}
                            className="no-copy noes-dropdown-item"
                            onClick={() => {
                              handleSelectOption(item);
                            }}
                            style={{
                              // margin: "2px",
                              paddingTop: "2px",
                              paddingBottom: "2px",
                              paddingLeft: "8px",
                              paddingRight: "7px",
                            }}
                          >
                            {item.display}
                          </div>
                        ))}
                      {maxToDisplayAtOnce &&
                      filteredOptions.length > maxToDisplayAtOnce ? (
                        <div
                          style={{
                            paddingTop: "2px",
                            paddingBottom: "2px",
                            paddingLeft: "8px",
                            paddingRight: "7px",
                          }}
                        >
                          {maxToDisplayAtOnce}/{filteredOptions.length} results
                          displayed. Start typing to see more results.
                        </div>
                      ) : (
                        ""
                      )}
                    </>
                  ) : (
                    showNoResultsMessage && (
                      <div
                        style={{
                          // display: "grid",
                          // placeContent: "center",
                          // padding: "2rem",
                          padding: ".5rem",
                          ...noResultsMessageStyleMerge,
                        }}
                      >
                        {/* {loading ? (
                <div
                  className="spinner-border"
                  style={{
                    borderWidth: ".15em",
                    color: "gray",
                    width: "1.5em",
                    height: "1.5em",
                    // backgroundColor: "green",
                  }}
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              ) : (
                noResultsMessage
              )} */}
                        {noResultsMessage}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
